import React, { useState, useEffect } from 'react'
import { gtmEvent } from 'lib/gtm'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CircularProgress from '@material-ui/core/CircularProgress'
import Chip from '@material-ui/core/Chip'
import { mdiTrophyVariant } from '@mdi/js'
import ChecklistBlack from 'images/checklist_black.svg'
import Icon from '@mdi/react'
import LeadsWidget from './leadsWidget'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { Grid } from '@material-ui/core'

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    margin: '0 60px',
    maxWidth: '1300px',
    [theme.breakpoints.down('xs')]: {
      margin: '10px',
    },
  },
  contentBox: ({ allStepsCompleted }) => ({
    display: 'flex',
    justifyContent: 'space-around',
    [theme.breakpoints.down('md')]: {
      flexDirection: allStepsCompleted ? 'column-reverse' : 'column',
    },
  }),
  mainSteps: {
    flexBasis: '100%',
    [theme.breakpoints.down('md')]: {
      flexBasis: '100%',
      margin: `0 ${theme.spacing(1)}px`,
    },
  },
  progressCol: {
    flexBasis: '100%',
    marginLeft: theme.spacing(2.5),
    [theme.breakpoints.down('md')]: {
      flexBasis: '100%',
      margin: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    },
  },
  welcomeSection: {
    padding: `0 ${theme.spacing(2)}px`,
    [theme.breakpoints.down('md')]: {
      padding: `0 ${theme.spacing(1)}px`,
    },
  },
  accordion: {
    margin: `${theme.spacing(1.5)}px 0`,
    [theme.breakpoints.down('md')]: {
      margin: `${theme.spacing(2)}px 0`,
    },
  },
}))

const Dashboard = ({ steps, progress, currentOpenStep }) => {
  const [isAvgResponseEnabled] = useFeatureFlags([
    flags.FLAG_AVG_RESPONSE_ENABLED,
  ])
  useEffect(() => {
    gtmEvent({
      event: 'onboarding-click',
      onboardingCategory: 'onboarding-dashboard',
      onboardingAction: 'dashboard-loaded',
      onboardingLabel: 'dashboard-loaded',
    })
  }, [])

  const allStepsCompleted = steps?.every(step => step.isComplete)
  const [expanded, setExpanded] = useState(
    currentOpenStep && currentOpenStep.id,
  )
  const classes = useStyles({ allStepsCompleted })

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.welcomeSection}>
        <Typography
          variant="h4"
          style={{ fontWeight: 500, marginBottom: '20px' }}
          color="primary"
        >
          Welcome to PGA Coach
        </Typography>
        <Typography variant="body1" style={{ marginBottom: '20px' }}>
          PGA Coach gives you a full set of tools to run your business. <br />
          We provide everything from the marketing to generate new leads to the
          technology to run more efficiently.
        </Typography>
        <Typography variant="body1" style={{ marginBottom: '20px' }}>
          Take full advantage of the PGA Coach platform by completing the steps
          below
        </Typography>
      </Box>
      <Box className={classes.contentBox}>
        <Box className={classes.mainSteps}>
          {steps.map(
            (
              {
                id,
                label,
                DescriptionTitle,
                DescriptionComp,
                isComplete,
                timeToComplete,
                icon,
                hasCoachProfile,
              },
              index,
            ) => (
              <Accordion
                key={id}
                expanded={expanded === id}
                onChange={handleChange(id)}
                className={classes.accordion}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  style={{ padding: '20px' }}
                >
                  {icon ||
                    (isComplete ? (
                      <CheckCircleIcon
                        style={{ color: 'green' }}
                        fontSize="large"
                      />
                    ) : (
                      <img src={ChecklistBlack} alt="not-complete" />
                    ))}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    style={{ width: '100%' }}
                  >
                    <Typography
                      style={{ alignSelf: 'center', marginLeft: '10px' }}
                    >
                      {label}
                    </Typography>
                    {!isComplete ? <Chip label={timeToComplete} /> : null}
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: 'column' }}>
                  {DescriptionTitle ? <DescriptionTitle /> : null}
                  <DescriptionComp
                    isComplete={isComplete}
                    hasCoachProfile={hasCoachProfile}
                  />
                </AccordionDetails>
              </Accordion>
            ),
          )}
        </Box>
        <Box className={classes.progressCol}>
          <Card style={{ minWidth: 275 }}>
            <Grid container direction="column" alignItems="center">
              <Typography
                variant="h5"
                style={{ margin: '5px', fontWeight: 500 }}
              >
                Progress
              </Typography>
            </Grid>
            <CardContent
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box position="relative" display="inline-flex">
                <CircularProgress
                  variant="determinate"
                  color="secondary"
                  size={120}
                  value={progress}
                />
                <Box
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  position="absolute"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon path={mdiTrophyVariant} color="secondary" size={1.75} />
                  <Grid>
                    <Typography
                      variant="h6"
                      component="div"
                      color="textSecondary"
                    >
                      {`${progress}%`}
                    </Typography>
                  </Grid>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {isAvgResponseEnabled ? <LeadsWidget /> : null}
        </Box>
      </Box>
    </Box>
  )
}

export default Dashboard
